import React, { useEffect, useState, useRef } from 'react';
import { Toast, ToastContainer as BSToastContainer, Modal, Button, Spinner as BSSpinner } from 'react-bootstrap';
import { subscribeLoading, subscribeToasts, dismissToast } from './lib';

export const Spinner = ({ size = 'md', label = '', inline = false }) => {
  const sizeMap = {
    xs: { w: 14, h: 14, bw: 2 },
    sm: { w: 20, h: 20, bw: 2 },
    md: { w: 40, h: 40, bw: 3 },
    lg: { w: 64, h: 64, bw: 4 },
  };
  const s = sizeMap[size] || sizeMap.md;
  return (
    <span className={`bv-spinner-wrap ${inline ? 'bv-spinner-inline' : ''}`} role="status" aria-label={label || 'Cargando'}>
      <span
        className="bv-spinner"
        style={{
          width: s.w,
          height: s.h,
          borderWidth: s.bw,
        }}
      />
      {label && <span className="bv-spinner-label">{label}</span>}
    </span>
  );
};

export const FullScreenSpinner = ({ label = 'Cargando...' }) => (
  <div className="bv-fullscreen-spinner" role="status" aria-label={label}>
    <img
      src="https://cdn.shopify.com/s/files/1/1437/8626/files/Homepage_0004_Falcon.png?v=1614320785"
      alt="Birdman"
      className="bv-spinner-logo"
    />
    <Spinner size="lg" />
    <div className="bv-spinner-text">{label}</div>
  </div>
);

export const Skeleton = ({ width = '100%', height = '1rem', rounded = false, className = '', style = {} }) => (
  <span
    className={`bv-skeleton ${rounded ? 'bv-skeleton-rounded' : ''} ${className}`}
    style={{ width, height, ...style }}
    aria-hidden="true"
  />
);

export const SkeletonCard = () => (
  <div className="bv-skeleton-card">
    <div className="bv-skeleton-card-header">
      <Skeleton width="40%" height="1.5rem" rounded />
      <Skeleton width="80px" height="1.3rem" rounded />
    </div>
    <div className="bv-skeleton-card-body">
      <div className="bv-skeleton-chart-row">
        <div className="bv-skeleton-chart">
          <Skeleton width="140px" height="140px" style={{ borderRadius: '50%' }} />
          <Skeleton width="80px" height="0.9rem" className="mt-2" rounded />
        </div>
        <div className="bv-skeleton-chart">
          <Skeleton width="140px" height="140px" style={{ borderRadius: '50%' }} />
          <Skeleton width="80px" height="0.9rem" className="mt-2" rounded />
        </div>
      </div>
      <div className="bv-skeleton-chips">
        <Skeleton width="110px" height="1.6rem" rounded />
        <Skeleton width="140px" height="1.6rem" rounded />
        <Skeleton width="90px" height="1.6rem" rounded />
        <Skeleton width="160px" height="1.6rem" rounded />
      </div>
    </div>
  </div>
);

export const SkeletonForm = () => (
  <div className="bv-skeleton-form">
    <Skeleton width="180px" height="1.5rem" rounded className="mb-4" />
    <div className="bv-skeleton-form-row">
      <Skeleton width="100%" height="3rem" rounded />
      <Skeleton width="100%" height="3rem" rounded />
    </div>
    <div className="bv-skeleton-form-row">
      <Skeleton width="100%" height="3rem" rounded />
      <Skeleton width="100%" height="3rem" rounded />
    </div>
    <Skeleton width="100%" height="3rem" rounded className="mb-3" />
    <Skeleton width="160px" height="2.5rem" rounded />
  </div>
);

export const SkeletonTable = ({ rows = 5, cols = 7 }) => (
  <div className="bv-skeleton-table">
    <div className="bv-skeleton-table-header">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} width="100%" height="1.1rem" rounded />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="bv-skeleton-table-row">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} width="100%" height="1.4rem" rounded />
        ))}
      </div>
    ))}
  </div>
);

export const SkeletonStats = ({ count = 3 }) => (
  <div className="bv-skeleton-stats">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bv-skeleton-stat-card">
        <Skeleton width="48px" height="48px" rounded />
        <Skeleton width="70%" height="0.9rem" className="mt-3" rounded />
        <Skeleton width="40%" height="2rem" className="mt-2" rounded />
      </div>
    ))}
  </div>
);

export const LoadingBar = () => {
  const [active, setActive] = useState(false);
  useEffect(() => subscribeLoading(setActive), []);
  return <div className={`bv-loading-bar ${active ? 'active' : ''}`} aria-hidden="true" />;
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  useEffect(() => subscribeToasts(setToasts), []);

  const icon = (v) =>
    ({
      success: 'bi-check-circle-fill',
      danger: 'bi-x-circle-fill',
      warning: 'bi-exclamation-triangle-fill',
      info: 'bi-info-circle-fill',
    }[v] || 'bi-info-circle-fill');

  return (
    <BSToastContainer position="top-end" className="p-3 bv-toast-container">
      {toasts.map((t) => (
        <Toast
          key={t.id}
          onClose={() => dismissToast(t.id)}
          className={`bv-toast bv-toast-${t.variant}`}
        >
          <Toast.Header closeButton>
            <i className={`bi ${icon(t.variant)} me-2 bv-toast-icon-${t.variant}`} />
            <strong className="me-auto">{t.title}</strong>
          </Toast.Header>
          {t.message && <Toast.Body>{t.message}</Toast.Body>}
        </Toast>
      ))}
    </BSToastContainer>
  );
};

export const ConfirmDialog = ({
  show,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'primary',
  icon = 'bi-question-circle-fill',
  onConfirm,
  onCancel,
  loading = false,
}) => (
  <Modal show={show} onHide={loading ? undefined : onCancel} centered backdrop="static" className="bv-modal">
    <Modal.Header closeButton={!loading}>
      <Modal.Title>
        <i className={`bi ${icon} me-2 bv-confirm-icon-${variant}`} />
        {title}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>{message}</Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" onClick={onCancel} disabled={loading}>
        {cancelText}
      </Button>
      <Button variant={variant} onClick={onConfirm} disabled={loading}>
        {loading && <BSSpinner as="span" size="sm" className="me-2" animation="border" />}
        {confirmText}
      </Button>
    </Modal.Footer>
  </Modal>
);

export const ResultDialog = ({
  show,
  variant = 'success',
  title,
  message,
  buttonText = 'Entendido',
  onClose,
}) => {
  const iconByVariant = {
    success: 'bi-check-circle-fill',
    danger: 'bi-x-circle-fill',
    warning: 'bi-exclamation-triangle-fill',
    info: 'bi-info-circle-fill',
  };
  const buttonVariant = variant === 'danger' ? 'danger' : variant === 'warning' ? 'warning' : 'primary';
  return (
    <Modal show={show} onHide={onClose} centered className="bv-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`bi ${iconByVariant[variant] || iconByVariant.info} me-2 bv-confirm-icon-${variant}`} />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{message}</Modal.Body>
      <Modal.Footer>
        <Button variant={buttonVariant} onClick={onClose}>
          {buttonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  handleReload = () => {
    window.location.reload();
  };
  render() {
    if (this.state.hasError) {
      return (
        <div className="bv-error-boundary">
          <div className="bv-error-card">
            <i className="bi bi-exclamation-octagon-fill bv-error-icon" />
            <h2>Algo salió mal</h2>
            <p>Ocurrió un error inesperado al renderizar la aplicación. Por favor recarga la página.</p>
            {this.state.error && (
              <details className="bv-error-details">
                <summary>Detalles técnicos</summary>
                <pre>{String(this.state.error?.stack || this.state.error)}</pre>
              </details>
            )}
            <Button variant="primary" onClick={this.handleReload}>
              <i className="bi bi-arrow-clockwise me-2" />
              Recargar
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const useDebounce = (value, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export const useMountedRef = () => {
  const ref = useRef(true);
  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);
  return ref;
};
